import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'

import { getSignature } from 'utils/getSignature'

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import {
  createEmployeeProfileBody,
  CreateEmployeeProfileInput,
  Employee,
  EmployeeApiResponse,
  EmployeesApiResponse,
  EmployeesResponse,
  FundResponse,
  normalizeEmployee,
  normalizeEmployeesResponse,
} from './type'

export const queryEmployees = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    createEmployeeProfile: builder.mutation<Employee, { fundId: string; profile: CreateEmployeeProfileInput }>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const url = apiPath.fundEmployee(arg.fundId)
        const body = createEmployeeProfileBody(arg.profile)
        const { deadline, signature } = await getSignature({
          payload: body,
          primaryType: 'CreateFundEmployee',
          url,
        })

        const result = await baseQuery({
          body,
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: normalizeEmployee(result.data as EmployeeApiResponse) }
      },
    }),
    getEmployees: builder.query<EmployeesResponse, { fundId?: string }>({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        let url: string

        if (_arg.fundId) {
          url = apiPath.fundEmployee(_arg.fundId)
        } else {
          const fundResponse = await baseQuery(apiPath.fund)
          if (!fundResponse.data) {
            return { error: fundResponse.error as FetchBaseQueryError }
          }
          const fund = fundResponse.data as FundResponse
          const fundId = fund.data[0]?.id
          if (!fundId) {
            return { error: { error: 'Fund not found', status: 'CUSTOM_ERROR' } }
          }

          url = apiPath.fundEmployee(fundId)
        }

        const { deadline, signature } = await getSignature({
          primaryType: 'GetFundEmployees',
          url,
        })

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: normalizeEmployeesResponse(result.data as EmployeesApiResponse) }
      },
    }),
  }),
  reducerPath: 'employees',
})

export const { useCreateEmployeeProfileMutation, useGetEmployeesQuery, useLazyGetEmployeesQuery } = queryEmployees
